package com.hyperether.goodjob.scenes.contacts

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material.Divider
import androidx.compose.material.Text
import androidx.compose.material3.DrawerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavHostController
import com.hyperether.goodjob.Dp
import com.hyperether.goodjob.Sp
import com.hyperether.goodjob.getPlatformChecker
import com.hyperether.goodjob.log
import com.hyperether.goodjob.navigation.AddNewContact
import com.hyperether.goodjob.repository.IndustryManager
import com.hyperether.goodjob.repository.prefs.PrefsManager
import com.hyperether.goodjob.resources.Res
import com.hyperether.goodjob.resources.add_a_contact
import com.hyperether.goodjob.resources.cancel
import com.hyperether.goodjob.resources.contact
import com.hyperether.goodjob.resources.contacts
import com.hyperether.goodjob.resources.edit
import com.hyperether.goodjob.resources.error
import com.hyperether.goodjob.resources.no_contacts_available
import com.hyperether.goodjob.resources.ok
import com.hyperether.goodjob.resources.success
import com.hyperether.goodjob.resources.total
import com.hyperether.goodjob.scenes.components.ErrorDialog
import com.hyperether.goodjob.scenes.components.FilterButton
import com.hyperether.goodjob.scenes.components.MobileHeaderSearchAdd
import com.hyperether.goodjob.scenes.components.PopUpDialog
import com.hyperether.goodjob.scenes.components.SearchField
import com.hyperether.goodjob.scenes.contacts.components.ContactsList
import com.hyperether.goodjob.scenes.contacts.components.EditContactsDialog
import com.hyperether.goodjob.scenes.register.FullScreenProgressBar
import com.hyperether.goodjob.theme.TextColor
import com.hyperether.goodjob.util.Constants.DEFAULT_CONTACT_ID
import com.hyperether.goodjob.util.FilterDialogStateManager
import com.hyperether.resources.stringResource

@Composable
fun ContactsScreen(
    navHostController: NavHostController,
    isMobileView: Boolean,
    viewModel: ContactsViewModel,
    drawerState: DrawerState,
    prefsManager: PrefsManager
) {

    val contacts by viewModel.contacts.collectAsStateWithLifecycle()
    val allIndustries by IndustryManager.industries.collectAsStateWithLifecycle()
    val sites = viewModel.sites.collectAsState(emptyList())
    val isInSearchMode = remember { mutableStateOf(false) }
    val searchQuery = remember { mutableStateOf("") }
    log.d("ContactsViewModel") { "ContactsScreen recompose contactList: ${contacts.size}" }

    LaunchedEffect(Unit) {
        viewModel.showEditPopUp.value = false
        viewModel.isInEditMode.value = false
        FilterDialogStateManager.setDialogShowing(false)
        if (isMobileView)
            viewModel.loadRemoteData()
    }

    viewModel.refreshFilter(contacts)

    if (isMobileView) {
        Box(modifier = Modifier.fillMaxSize()) {
            Column {
                MobileHeaderSearchAdd(
                    title = stringResource(Res.string.contacts),
                    drawerState = drawerState,
                    scope = rememberCoroutineScope(),
                    add = {
                        if (getPlatformChecker().isMobile()) {
                            navHostController.navigate(AddNewContact(DEFAULT_CONTACT_ID))
                        } else {
                            navHostController.navigate(AddNewContact(""))
                        }
                    },
                    search = {
                        isInSearchMode.value = true
                    },
                    onCancelSearch = {
                        isInSearchMode.value = false
                        searchQuery.value = ""
                    },
                    user = prefsManager.getUser()
                )
                if (contacts.isNotEmpty()) {
                    if (isInSearchMode.value) {
                        Spacer(modifier = Modifier.height(Dp(15f)))
                        SearchField(searchQuery)
                    } else {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.fillMaxWidth()
                                .padding(horizontal = Dp(16f), vertical = Dp(10f))
                        ) {
                            if (!viewModel.isInEditMode.value) {
                                DisableSelection {
                                    Text(
                                        text = stringResource(Res.string.edit),
                                        fontSize = Sp(14f),
                                        color = TextColor,
                                        modifier = Modifier.clickable {
                                            viewModel.isInEditMode.value = true
                                            viewModel.showEditPopUp.value = true
                                        }.weight(1f),
                                        textAlign = TextAlign.Start
                                    )
                                }
                            } else {
                                DisableSelection {
                                    Text(
                                        text = stringResource(Res.string.cancel),
                                        fontSize = Sp(14f),
                                        color = TextColor,
                                        modifier = Modifier.clickable {
                                            viewModel.isInEditMode.value = false
                                            viewModel.showEditPopUp.value = false
                                            viewModel.selectedContacts.clear()
                                        }.weight(1f),
                                        textAlign = TextAlign.Start
                                    )
                                }
                            }

                            if (!viewModel.isInEditMode.value) {
                                val leadingText =
                                    if (viewModel.contactList.size == 1) stringResource(Res.string.contact) else stringResource(
                                        Res.string.contacts
                                    )
                                Text(
                                    text = "${stringResource(Res.string.total)}: ${viewModel.contactList.size} $leadingText",
                                    fontSize = Sp(14f),
                                    color = Color.LightGray,
                                    maxLines = 1,
                                    fontStyle = FontStyle.Normal,
                                    textAlign = TextAlign.Center
                                )
                            }

                            Row(
                                horizontalArrangement = Arrangement.End,
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.weight(1f)
                            ) {
                                FilterButton()
                            }
                        }
                        Divider(
                            modifier = Modifier.height(Dp(2f))
                                .background(Color.LightGray.copy(alpha = 0.1f))
                        )
                    }
                }
                if (viewModel.contactList.isEmpty()) {
                    androidx.compose.material3.Text(
                        text = stringResource(Res.string.no_contacts_available),
                        color = Color.Black,
                        fontSize = Sp(24f),
                        textAlign = TextAlign.Center,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.fillMaxWidth()
                            .padding(top = Dp(60f), start = Dp(16f), end = Dp(16f))
                    )
                    androidx.compose.material3.Text(
                        text = stringResource(Res.string.add_a_contact),
                        color = Color.Gray,
                        textAlign = TextAlign.Center,
                        fontSize = Sp(16f),
                        modifier = Modifier.padding(top = Dp(20f), start = Dp(16f), end = Dp(16f))
                    )
                } else {
                    val filteredContactsSearch = if (searchQuery.value.isNotBlank()) {
                        viewModel.contactList.filter { contact ->
                            contact.name?.contains(
                                searchQuery.value,
                                ignoreCase = true
                            ) == true || contact.personName?.contains(
                                searchQuery.value, ignoreCase = true
                            ) == true || contact.addressObject?.address?.contains(
                                searchQuery.value, ignoreCase = true
                            ) == true
                        }
                    } else {
                        viewModel.contactList
                    }
                    val sortedList = filteredContactsSearch.sortedBy { contact ->
                        (contact.name ?: contact.email)?.lowercase()
                    }
                    ContactsList(
                        contacts = sortedList,
                        contactSites = sites.value,
                        selectedContacts = viewModel.selectedContacts,
                        isInEditMode = viewModel.isInEditMode.value,
                        navHostController = navHostController
                    )
                }

            }
            if (viewModel.showEditPopUp.value) {
                Box(
                    modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.05f))
                )

                Box(
                    modifier = Modifier.align(Alignment.BottomEnd)
                ) {
                    EditContactsDialog(
                        showDialog = viewModel.showEditPopUp.value,
                        contactList = viewModel.selectedContacts,
                        onDelete = {
                            viewModel.deleteSelectedContacts()
                            viewModel.showEditPopUp.value = false
                            viewModel.isInEditMode.value = false
                        },
                        onExportList = {},
                        onImportList = {})
                }
            }
            if (viewModel.showPopUpSuccess.value) {
                PopUpDialog(
                    onDismiss = { viewModel.showPopUpSuccess.value = false },
                    title = stringResource(Res.string.success).uppercase(),
                    message = viewModel.successApiText.value,
                    onPositiveBtnClicked = { viewModel.showPopUpSuccess.value = false },
                    positiveBtnText = stringResource(Res.string.ok)
                )
            }
            if (viewModel.showPopUpError.value) {
                ErrorDialog(
                    onDismiss = { viewModel.showPopUpError.value = false },
                    title = stringResource(Res.string.error).uppercase(),
                    message = viewModel.errorText.value
                )
            }
            if (viewModel.showLoader.value) {
                FullScreenProgressBar()
            }
        }
    } else {
        SelectionContainer {
            ContactsWeb(
                navController = navHostController,
                viewModel = viewModel,
                sites = sites.value,
                onFilterClick = { FilterDialogStateManager.setDialogShowing(true) })
        }
    }

    if (FilterDialogStateManager.dialogShowing.value) {
        viewModel.refreshFilter(contacts)
        val cities = contacts.mapNotNull { it.addressObject?.city }.distinct()
        val industriesInContacts = contacts.mapNotNull { it.industry?.trim() }.toSet()
        val industries = allIndustries.filter { it.trim() in industriesInContacts }
        val contactTypes = contacts.mapNotNull { it.contactType }.map { contactType ->
            val type =
                viewModel.contactTypes.entries.find { item -> item.key == contactType }?.value
            if (type != null) {
                stringResource(type)
            } else {
                contactType
            }
        }.distinct()

        FilterContactsDialog(
            cities = cities,
            industries = industries,
            contactTypes = contactTypes,
            onDismiss = {
                FilterDialogStateManager.setDialogShowing(false)
            },
            viewModel = viewModel,
            isMobileView = isMobileView,
            onApply = { selectedCities, selectedIndustries, selectedContactTypes ->
                FilterDialogStateManager.setDialogShowing(false)
                viewModel.selectedCities.value = selectedCities
                viewModel.selectedIndustries.value = selectedIndustries
                viewModel.selectedContactTypes.value = selectedContactTypes
                viewModel.filterContacts()
            })
    }
}